import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Swords, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  HelpCircle,
  ArrowRight,
  Wand2,
  Shirt,
  Crown,
  Palette,
  Gem,
  User,
  UserCircle,
  Gamepad2,
  Tv,
  BookOpen,
  Film
} from 'lucide-react';
import { toast } from 'sonner';

// Cosplay type options
const COSPLAY_TYPES = [
  { value: 'costume_completo', label: 'Costume completo', icon: Shirt },
  { value: 'armatura', label: 'Armatura / Props', icon: Shield },
  { value: 'accessori', label: 'Accessori (armi, gioielli)', icon: Swords },
  { value: 'parrucca', label: 'Parrucca styling', icon: Sparkles },
  { value: 'makeup_prosthetics', label: 'Makeup / Prosthetics', icon: Palette },
  { value: 'mantello_cappa', label: 'Mantello / Cappa', icon: Crown },
  { value: 'scarpe_stivali', label: 'Scarpe / Stivali', icon: Gem },
  { value: 'altro', label: 'Altro', icon: Wand2 },
];

// Character source options
const CHARACTER_SOURCES = [
  { value: 'anime_manga', label: 'Anime / Manga', icon: BookOpen },
  { value: 'videogioco', label: 'Videogioco', icon: Gamepad2 },
  { value: 'film_serie', label: 'Film / Serie TV', icon: Film },
  { value: 'fumetto', label: 'Fumetto', icon: BookOpen },
  { value: 'originale', label: 'Personaggio originale', icon: Sparkles },
  { value: 'storico', label: 'Storico / Fantasy', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Tv },
];

// Gift recipient options (for whom)
const COSPLAY_RECIPIENTS = [
  { value: 'me_stesso', label: 'Per me stesso/a', icon: Sparkles },
  { value: 'uomo', label: 'Per un uomo', icon: User },
  { value: 'donna', label: 'Per una donna', icon: UserCircle },
  { value: 'bambino', label: 'Per un bambino', icon: User },
  { value: 'bambina', label: 'Per una bambina', icon: UserCircle },
  { value: 'coppia', label: 'Per una coppia (duo)', icon: Users },
  { value: 'gruppo', label: 'Per un gruppo', icon: Users },
  { value: 'altro', label: 'Altro', icon: Wand2 },
];

// Budget options - aligned with RequestsPage
const BUDGET_OPTIONS = [
  { value: '0-50', label: '€0 - €50', description: 'Accessori semplici' },
  { value: '50-100', label: '€50 - €100', description: 'Props base' },
  { value: '100-250', label: '€100 - €250', description: 'Costume semplice' },
  { value: '250-500', label: '€250 - €500', description: 'Costume elaborato' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Costume completo' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Armatura / set completo' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Cosplay professionale' },
  { value: '5000+', label: '€5.000+', description: 'Progetto complesso / gruppo' },
];

// Timeline options - aligned with RequestsPage
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: '3months', label: '3 Mesi', description: 'Senza fretta' },
  { value: '6months', label: '6 Mesi', description: 'Progetto lungo' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example cosplays for showcase
const COSPLAY_EXAMPLES = [
  {
    idea: "Armatura di Cloud Strife per fiera",
    result: "Armatura completa in EVA foam con spada Buster Sword",
    image: "https://images.unsplash.com/flagged/photo-1569517282304-d1e2baf737e4?w=400&h=300&fit=crop",
    category: "Armatura"
  },
  {
    idea: "Costume Sailor Moon per cosplay contest",
    result: "Costume sartoriale con fiocchi e tiara artigianale",
    image: "https://images.unsplash.com/photo-1727409059001-e7a3e40de86e?w=400&h=300&fit=crop",
    category: "Costume"
  },
  {
    idea: "Props Keyblade Kingdom Hearts",
    result: "Keyblade in resina con LED integrati e supporto",
    image: "https://images.unsplash.com/photo-1747238748512-a4d0f8fc49f3?w=400&h=300&fit=crop",
    category: "Props"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho partecipato al Lucca Comics con un'armatura incredibile. Tutti mi chiedevano dove l'avessi comprata!",
    author: "Marco T.",
    location: "Firenze"
  },
  {
    text: "Finalmente un costume che calza perfettamente. L'artigiano ha seguito ogni mia indicazione.",
    author: "Giulia S.",
    location: "Milano"
  },
  {
    text: "Il mio cosplay di Link ha vinto il primo premio! Qualità sartoriale italiana al top.",
    author: "Andrea R.",
    location: "Roma"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Puoi descrivere il tuo progetto cosplay e ricevere proposte senza alcun costo."
  },
  {
    question: "Sono obbligato a comprare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi l'artigiano perfetto per il tuo progetto."
  },
  {
    question: "Gli artigiani sono esperti di cosplay?",
    answer: "Sì, su Rarifex trovi sarti, armaioli, prop maker e artisti specializzati in costumi e accessori per cosplay e rievocazione."
  },
  {
    question: "Posso inviare immagini di riferimento?",
    answer: "Certamente! Puoi allegare screenshot del personaggio, fan art, o qualsiasi immagine che aiuti a comunicare la tua visione."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un cosplay?",
    answer: "Dipende dalla complessità. Un accessorio può richiedere 1-2 settimane, mentre un costume completo con armatura può richiedere 2-3 mesi."
  }
];

export default function CosplayRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cosplay_type: '',
    character_source: '',
    gift_recipient: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Cosplay su Misura | Artigiani Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `cosplay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il tuo progetto cosplay');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      // Save form data to localStorage for after login
      localStorage.setItem('rarifex_pending_cosplay_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      // Build title from form data
      const cosplayTypeLabel = COSPLAY_TYPES.find(t => t.value === formData.cosplay_type)?.label || '';
      const sourceLabel = CHARACTER_SOURCES.find(s => s.value === formData.character_source)?.label || '';
      const recipientLabel = COSPLAY_RECIPIENTS.find(r => r.value === formData.gift_recipient)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (cosplayTypeLabel) details.push(`🎭  Tipo: ${cosplayTypeLabel}`);
      if (sourceLabel) details.push(`🎬  Origine: ${sourceLabel}`);
      if (recipientLabel) details.push(`👤  Per chi: ${recipientLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `Cosplay ${cosplayTypeLabel} - ${sourceLabel}`.trim() || 'Cosplay personalizzato',
        description: fullDescription,
        category: 'Cosplay',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      // Use backend API to bypass RLS issues
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('cosplay-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Cosplay su Misura | Costumi e Props Artigianali | Rarifex</title>
        <meta name="description" content="Vuoi un cosplay unico? Artigiani italiani creano costumi, armature e props su misura per te. Descrivi il tuo personaggio e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="cosplay su misura, costume cosplay, armatura cosplay, props cosplay, costume artigianale, cosplay italiano, cosplay personalizzato, costume anime, costume videogioco" />
        <link rel="canonical" href="https://www.rarifex.com/cosplay-su-misura" />
        <meta property="og:title" content="Cosplay su Misura | Artigiani Italiani | Rarifex" />
        <meta property="og:description" content="Vuoi un cosplay unico? Artigiani italiani creano costumi, armature e props su misura per te. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/cosplay-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-indigo-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Swords className="w-4 h-4" />
                    Cosplay su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il tuo{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-500">
                      cosplay perfetto
                    </span>
                    {' '}inizia qui
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani realizzano costumi, armature e props su misura per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi il personaggio che vuoi interpretare e ricevi proposte da sarti, armaioli e prop maker esperti.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani specializzati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="cosplay-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Nome del personaggio / progetto
                        </Label>
                        <Input
                          placeholder="Es: Cloud Strife - Final Fantasy VII"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="cosplay-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il tuo progetto cosplay *
                        </Label>
                        <Textarea
                          placeholder="Racconta cosa vuoi realizzare... Descrivi il personaggio, i dettagli importanti, le tue misure se necessario, materiali preferiti, eventi a cui parteciperai..."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="cosplay-description-textarea"
                        />
                      </div>
                      
                      {/* Cosplay Type & Character Source */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Cosa ti serve?
                          </Label>
                          <select
                            value={formData.cosplay_type}
                            onChange={(e) => handleInputChange('cosplay_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            data-testid="cosplay-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {COSPLAY_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Origine del personaggio
                          </Label>
                          <select
                            value={formData.character_source}
                            onChange={(e) => handleInputChange('character_source', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            data-testid="cosplay-source-select"
                          >
                            <option value="">Seleziona...</option>
                            {CHARACTER_SOURCES.map(source => (
                              <option key={source.value} value={source.value}>{source.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Recipient & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Per chi è?</Label>
                          <select
                            value={formData.gift_recipient}
                            onChange={(e) => handleInputChange('gift_recipient', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            data-testid="cosplay-recipient-select"
                          >
                            <option value="">Seleziona...</option>
                            {COSPLAY_RECIPIENTS.map(rec => (
                              <option key={rec.value} value={rec.value}>{rec.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            data-testid="cosplay-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          data-testid="cosplay-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (consigliate!)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="cosplay-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica screenshot o fan art ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-purple-200"
                        data-testid="cosplay-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Swords className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Il cosplay perfetto non si trova online
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-purple-400">✗</span>
                  I costumi prefabbricati non calzano mai bene
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-purple-400">✗</span>
                  I dettagli importanti sono sempre sbagliati
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-purple-400">✗</span>
                  La qualità dei materiali è deludente
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un cosplay che fa la differenza, serve un artigiano.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Il tuo cosplay,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-500">
                  realizzato su misura
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Sarti, armaioli e prop maker italiani pronti a realizzare il tuo progetto.
                <br />
                <strong className="text-slate-800">Tu descrivi, loro creano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi il personaggio</h3>
                  <p className="text-slate-600">Allega immagini e spiega cosa vuoi realizzare</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-indigo-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Artigiani ti propongono</h3>
                  <p className="text-slate-600">Ricevi preventivi da esperti di costumi e props</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il migliore</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a cosplay
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno realizzato i nostri artigiani
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {COSPLAY_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-purple-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  Vuoi qualcosa di simile? Descrivi il tuo progetto
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi il tuo cosplay</h3>
                    <p className="text-slate-600">
                      Racconta quale personaggio vuoi interpretare, allega immagini di riferimento e indica le tue misure se necessario.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Sarti, armaioli e prop maker vedono la tua richiesta e ti inviano preventivi dettagliati con foto dei loro lavori.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli l'artigiano perfetto</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, chatta con gli artigiani per discutere i dettagli. Nessun obbligo, scegli solo se sei soddisfatto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere un cosplay artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-purple-50">
                  <CheckCircle className="w-8 h-8 text-purple-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Calzata perfetta</h3>
                    <p className="text-sm text-slate-600">Realizzato sulle tue misure esatte</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-indigo-50">
                  <Sparkles className="w-8 h-8 text-indigo-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Dettagli accurati</h3>
                    <p className="text-sm text-slate-600">Ogni particolare curato come vuoi tu</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Materiali di qualità</h3>
                    <p className="text-sm text-slate-600">Tessuti e materiali selezionati</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i cosplayer
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-purple-400 text-purple-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center font-semibold text-purple-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`cosplay-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Swords className="w-16 h-16 text-purple-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a diventare il tuo personaggio?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Gli artigiani italiani aspettano la tua richiesta.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-purple-200"
                data-testid="cosplay-cta-final-button"
              >
                <Swords className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artigiani, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/cosplay-su-misura')}
                className="flex-1 bg-purple-500 hover:bg-purple-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
